/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import Table from "components/Table";
import { SimpleSelectInput } from "components/FormSelectInput";
import configData from "config/config.json";
import usePaginationHook from "hooks/usePagination";
import { useSnackbar } from "hooks/useSnackbar";
import sessionHelper from "services/sessionHelper";
import { defaultPageNo } from "utils/constants";
import { PermittedRoles } from "utils/permissions";
import { getUsersUnderMaintenance } from "services/userMaintenanceService";

const columns = [
  { id: "externalPlayerId", label: "External User ID" },
  { id: "playerName", label: "RGS Player Id" },
  { id: "operatorName", label: "Operator" },
  { id: "messageText", label: "Message" },
  { id: "reasonText", label: "Reason" },
  { id: "appliedByText", label: "Applied By" },
  { id: "appliedAtText", label: "Applied At" },
];

const UserMaintenance = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filterOperatorId, setFilterOperatorId] = useState("");

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems });

  const operatorOptions = sessionHelper.operatorOptions || [];
  const canUpdate = sessionHelper.hasRole(
    PermittedRoles.USER_MAINTENANCE.UPDATE_ROLES,
  );

  const operatorName = (operatorId: string) =>
    operatorOptions.find((option: any) => option.id === operatorId)?.label ||
    operatorId;

  const fetchData = async (operatorId = filterOperatorId) => {
    try {
      setLoading(true);
      const response = await getUsersUnderMaintenance({
        pageNo,
        pageSize,
        operatorId,
      });
      const page = response.data || {};
      setTotalItems(page.totalItems || 0);
      setListData(
        (page.items || []).map((item: any) => ({
          ...item,
          operatorName: operatorName(item.operatorId),
          messageText: item?.maintenance?.message || "-",
          reasonText: item?.maintenance?.reason || "-",
          appliedByText: item?.maintenance?.appliedBy || "-",
          appliedAtText: item?.maintenance?.appliedAt || "-",
        })),
      );
    } catch (error: any) {
      showSnackbar(
        error.message || "Failed to fetch users under maintenance",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const addUser = () => navigate(configData.routes.userMaintenance.create);

  const rowHref = (data: any) =>
    configData.routes.userMaintenance.edit?.replace(":id", data?.rgsPlayerId);

  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize]);

  return (
    <ContentShell
      title="User Maintenance"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <Box style={{ width: "200px" }}>
            <SimpleSelectInput
              name="filterOperatorId"
              placeholdertext="Filter by Operator"
              options={[{ id: "", label: "All Operators" }, ...operatorOptions]}
              value={filterOperatorId}
              onChange={(e: any) => {
                setFilterOperatorId(e?.target.value);
                onChangePage(defaultPageNo);
                fetchData(e?.target.value);
              }}
            />
          </Box>
          {canUpdate && <CustomButton onClick={addUser}>Add</CustomButton>}
        </Box>
      }
    >
      <Box>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={totalItems}
          highlightAltRow={true}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          rowHref={rowHref}
          actionHandlers={{}}
        />
      </Box>
    </ContentShell>
  );
};

export default UserMaintenance;
